
import React, { useState } from 'react';
import { Database, ShieldCheck, Users, Activity, Lock, Settings, FileText, PlusCircle, BarChart2, Gift, CheckCircle, Tag, Server, Table, FileSpreadsheet, Cpu, Store, Boxes, MoreHorizontal, ChevronDown, Bot } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserRole } from '../types';
import Logo from './Logo';

interface NavbarProps {
  userRole: UserRole; // The user's actual assigned role
}

interface NavItem {
  id: string;
  path: string;
  label: string;
  desktopLabel: string;
  icon: React.ReactNode;
  group?: string;            // desktop dropdown grouping (see GROUP_META)
  allowedRoles: UserRole[];
}

// Group metadata for the desktop menus. A nav item's `group` points here; items
// that share a group collapse into one dropdown (a group with a single visible
// item for the current role renders as a plain button instead).
const GROUP_META: Record<string, { label: string; icon: React.ReactNode }> = {
  Surveys: { label: 'Surveys', icon: <FileText /> },
  IoT:     { label: 'IoT',     icon: <Cpu /> },
  Silos:   { label: 'Silos',   icon: <Boxes /> },
  Earn:    { label: 'Earn',    icon: <Gift /> },
  Admin:   { label: 'Admin',   icon: <ShieldCheck /> },
};

// Utility items live on the right of the bar as compact icon buttons, out of the
// way of the primary role navigation.
const UTILITY_IDS = new Set(['privacy-vault', 'settings']);

const Navbar: React.FC<NavbarProps> = ({ userRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMore, setShowMore] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  // Define all possible navigation items with their allowed roles. `group` (optional)
  // controls how items collapse into desktop dropdown menus (see GROUP_META).
  const allNavItems: NavItem[] = [
    // --- Data Agent Routes ---
    {
      id: 'data',
      path: '/data',
      label: 'Input',
      desktopLabel: 'Data Input',
      icon: <Database />,
      allowedRoles: [UserRole.DATA_AGENT]
    },
    // --- Researcher Routes (Removed CTO) ---
    {
      id: 'campaigns',
      path: '/research/campaigns',
      label: 'Campaigns',
      desktopLabel: 'Campaigns',
      icon: <FileText />,
      group: 'Surveys',
      allowedRoles: [UserRole.RESEARCHER]
    },
    {
      id: 'create',
      path: '/research/create',
      label: 'Create',
      desktopLabel: 'New Survey',
      icon: <PlusCircle />,
      group: 'Surveys',
      allowedRoles: [UserRole.RESEARCHER]
    },
    {
      id: 'import',
      path: '/research/import',
      label: 'Import',
      desktopLabel: 'Import',
      icon: <FileSpreadsheet />,
      group: 'Surveys',
      allowedRoles: [UserRole.RESEARCHER]
    },
    {
      id: 'analytics',
      path: '/research/analytics',
      label: 'Analytics',
      desktopLabel: 'Analytics',
      icon: <BarChart2 />,
      group: 'Surveys',
      allowedRoles: [UserRole.RESEARCHER]
    },
    {
      id: 'gift',
      path: '/research/gift',
      label: 'Gift',
      desktopLabel: 'Gifting',
      icon: <Gift />,
      group: 'Surveys',
      allowedRoles: [UserRole.RESEARCHER]
    },
    {
      id: 'data-market',
      path: '/iot/market',
      label: 'Market',
      desktopLabel: 'Data Market',
      icon: <Store />,
      group: 'IoT',
      allowedRoles: [UserRole.RESEARCHER]
    },
    {
      id: 'devices',
      path: '/iot/devices',
      label: 'Devices',
      desktopLabel: 'IoT Devices',
      icon: <Cpu />,
      group: 'IoT',
      allowedRoles: [UserRole.RESEARCHER, UserRole.GENERAL_USER]
    },
    {
      id: 'my-silos',
      path: '/research/silos',
      label: 'Silos',
      desktopLabel: 'My Silos',
      icon: <Server />,
      group: 'Silos',
      allowedRoles: [UserRole.RESEARCHER]
    },
    // --- General User Routes ---
    {
      id: 'user-campaigns',
      path: '/earn/campaigns',
      label: 'Campaigns',
      desktopLabel: 'Campaigns',
      icon: <FileText />,
      group: 'Earn',
      allowedRoles: [UserRole.GENERAL_USER]
    },
    {
      id: 'user-gift',
      path: '/earn/gifts',
      label: 'Gift',
      desktopLabel: 'My Gifts',
      icon: <Gift />,
      group: 'Earn',
      allowedRoles: [UserRole.GENERAL_USER]
    },
    {
      id: 'user-mystery',
      path: '/earn/mystery',
      label: 'Boxes',
      desktopLabel: 'Mystery Boxes',
      icon: <Gift />,
      group: 'Earn',
      allowedRoles: [UserRole.GENERAL_USER]
    },
    // --- Silo Surveys (BYOI) — discover + take surveys on researchers' own silos ---
    {
      id: 'silo-surveys',
      path: '/silos',
      label: 'Silos',
      desktopLabel: 'Silo Surveys',
      icon: <Boxes />,
      group: 'Silos',
      allowedRoles: [UserRole.GENERAL_USER, UserRole.RESEARCHER, UserRole.CTO, UserRole.DATA_AGENT]
    },
    // --- CTO / Admin Exclusive Routes ---
    {
      id: 'cto-category',
      path: '/admin/categories',
      label: 'Category',
      desktopLabel: 'Categories',
      icon: <Tag />,
      group: 'Admin',
      allowedRoles: [UserRole.CTO]
    },
    {
      id: 'cto-source',
      path: '/admin/sources',
      label: 'Source',
      desktopLabel: 'Sources',
      icon: <Server />,
      group: 'Admin',
      allowedRoles: [UserRole.CTO]
    },
    {
      id: 'cto-data',
      path: '/admin/data-points',
      label: 'Ledger',
      desktopLabel: 'Public Ledger',
      icon: <Table />,
      group: 'Admin',
      allowedRoles: [UserRole.CTO, UserRole.DATA_AGENT]
    },
    {
      id: 'admin',
      path: '/admin',
      label: 'Team',
      desktopLabel: 'Team',
      icon: <Users />,
      group: 'Admin',
      allowedRoles: [UserRole.CTO]
    },
    {
      id: 'admin-agents',
      path: '/admin/agents',
      label: 'Agents',
      desktopLabel: 'Agent Keys',
      icon: <Bot />,
      group: 'Admin',
      allowedRoles: [UserRole.CTO]
    },
    // --- Privacy Vault (Common, per-user encrypted PII) ---
    {
      id: 'privacy-vault',
      path: '/privacy-vault',
      label: 'Vault',
      desktopLabel: 'Privacy Vault',
      icon: <Lock />,
      allowedRoles: [UserRole.DATA_AGENT, UserRole.RESEARCHER, UserRole.GENERAL_USER, UserRole.CTO, UserRole.AI_AGENT]
    },
    // --- Settings (Common) ---
    {
      id: 'settings',
      path: '/settings',
      label: 'Settings',
      desktopLabel: 'Settings',
      icon: <Settings />,
      allowedRoles: [UserRole.DATA_AGENT, UserRole.RESEARCHER, UserRole.GENERAL_USER, UserRole.CTO, UserRole.AI_AGENT]
    }
  ];

  // Filter routes to only those allowed for the current user
  const routes = allNavItems.filter(item => item.allowedRoles.includes(userRole));

  const currentPath = location.pathname;
  // Enhanced active route detection for sub-paths
  const activeRoute = routes.find(r => currentPath === r.path || (r.path !== '/' && currentPath.startsWith(r.path))) || routes[0];

  const handleNavigation = (path: string) => {
    // Switching tabs changes the path, and App.tsx's Outlet key={pathname} re-mounts
    // the view (reloading its data). Navigating to the same path uses replace so the
    // click still registers as an intentional (re)load.
    if (path === currentPath) {
      navigate(path, { replace: true });
    } else {
      navigate(path);
    }
    setOpenGroup(null);
  };

  // --- Desktop: collapse the role's routes into grouped menus -------------------
  // Utility items (Vault, Settings) are pulled out to the right of the bar. The rest
  // keep their first-appearance order; items sharing a `group` fold into one entry.
  const primaryRoutes = routes.filter(r => !UTILITY_IDS.has(r.id));
  const utilityRoutes = routes.filter(r => UTILITY_IDS.has(r.id));

  type DesktopEntry = { type: 'item'; item: NavItem } | { type: 'group'; group: string };
  const desktopEntries: DesktopEntry[] = [];
  const groupBuckets = new Map<string, NavItem[]>();
  for (const r of primaryRoutes) {
    if (r.group) {
      if (!groupBuckets.has(r.group)) { groupBuckets.set(r.group, []); desktopEntries.push({ type: 'group', group: r.group }); }
      groupBuckets.get(r.group)!.push(r);
    } else {
      desktopEntries.push({ type: 'item', item: r });
    }
  }

  const sized = (icon: React.ReactNode, size: number, extra: Record<string, any> = {}) =>
    React.cloneElement(icon as React.ReactElement<any>, { size, ...extra });

  const itemBtnClass = (active: boolean) =>
    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
      active ? 'bg-slate-800 text-white border border-slate-700' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
    }`;

  // A single top-bar button (used for standalone items and single-item groups).
  const DesktopButton = ({ route }: { route: NavItem }) => (
    <button onClick={() => handleNavigation(route.path)} className={itemBtnClass(activeRoute?.id === route.id)}>
      {sized(route.icon, 16)}
      {route.desktopLabel}
    </button>
  );

  // --- Mobile/tablet bottom bar: cap visible tabs, push rest into a "More" sheet.
  const MAX_TABS = 5;
  const useMore = routes.length > MAX_TABS;
  const primary = useMore ? routes.slice(0, MAX_TABS - 1) : routes;
  const overflow = useMore ? routes.slice(MAX_TABS - 1) : [];
  const moreActive = overflow.some(r => r.id === activeRoute?.id);

  return (
    <>
      {/* Top Navigation (Desktop) & Branding Header (Mobile) */}
      <nav className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo Section */}
            <div
              className="flex-shrink-0 flex items-center gap-2.5 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <Logo size={32} />
              <span className="font-bold text-lg tracking-tight text-white">TINANEY</span>
            </div>

            {/* Desktop Navigation (Center) — grouped dropdown menus. Phones and tablets
                use the touch-friendly bottom bar below. */}
            <div className="hidden lg:flex items-center justify-center flex-1">
              <div className="flex items-center gap-1">
                {desktopEntries.map((entry) => {
                  if (entry.type === 'item') return <DesktopButton key={entry.item.id} route={entry.item} />;

                  const items = groupBuckets.get(entry.group)!;
                  // A group with a single visible item for this role is just a button.
                  if (items.length === 1) return <DesktopButton key={items[0].id} route={items[0]} />;

                  const meta = GROUP_META[entry.group] ?? { label: entry.group, icon: <Boxes /> };
                  const groupActive = items.some(i => i.id === activeRoute?.id);
                  const open = openGroup === entry.group;
                  return (
                    <div key={entry.group} className="relative">
                      <button
                        onClick={() => setOpenGroup(open ? null : entry.group)}
                        className={itemBtnClass(groupActive)}
                      >
                        {sized(meta.icon, 16)}
                        {meta.label}
                        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                      </button>
                      {open && (
                        <div className="absolute left-0 mt-1.5 w-56 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-1.5 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                          {items.map((it) => {
                            const active = activeRoute?.id === it.id;
                            return (
                              <button
                                key={it.id}
                                onClick={() => handleNavigation(it.path)}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors ${
                                  active ? 'bg-slate-700/70 text-white' : 'text-slate-300 hover:bg-slate-700/40 hover:text-white'
                                }`}
                              >
                                {sized(it.icon, 16, { className: active ? 'text-indigo-400' : 'text-slate-400' })}
                                {it.desktopLabel}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: utility icon buttons (Vault, Settings) */}
            <div className="hidden lg:flex items-center gap-1">
              {utilityRoutes.map((route) => {
                const active = activeRoute?.id === route.id;
                return (
                  <button
                    key={route.id}
                    onClick={() => handleNavigation(route.path)}
                    title={route.desktopLabel}
                    aria-label={route.desktopLabel}
                    className={`p-2 rounded-md transition-colors duration-200 ${
                      active ? 'bg-slate-800 text-white border border-slate-700' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                    }`}
                  >
                    {sized(route.icon, 18)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </nav>

      {/* Backdrop to close an open desktop dropdown on outside click */}
      {openGroup && <div className="hidden lg:block fixed inset-0 z-30" onClick={() => setOpenGroup(null)} />}

      {/* Bottom Navigation Bar (phones + tablets) */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-md border-t border-slate-800 z-40 pb-safe">
        <div className="flex items-stretch h-16 px-1">
          {primary.map((route) => (
            <button
              key={route.id}
              onClick={() => handleNavigation(route.path)}
              className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-1 active:scale-95 transition-transform ${
                activeRoute?.id === route.id ? 'text-indigo-500' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {React.cloneElement(route.icon as React.ReactElement<any>, {
                size: 22,
                strokeWidth: activeRoute?.id === route.id ? 2.5 : 2,
              })}
              <span className="text-[10px] font-medium tracking-wide truncate max-w-full px-0.5">{route.label}</span>
            </button>
          ))}
          {useMore && (
            <button
              onClick={() => setShowMore(true)}
              className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-1 active:scale-95 transition-transform ${
                moreActive ? 'text-indigo-500' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <MoreHorizontal size={22} strokeWidth={moreActive ? 2.5 : 2} />
              <span className="text-[10px] font-medium tracking-wide">More</span>
            </button>
          )}
        </div>
      </div>

      {/* "More" sheet — remaining tabs (phones + tablets) */}
      {showMore && (
        <div className="lg:hidden fixed inset-0 z-50" onClick={() => setShowMore(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="absolute bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 rounded-t-2xl p-4 pb-safe animate-in slide-in-from-bottom duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-slate-700 rounded-full mx-auto mb-4" />
            <div className="grid grid-cols-4 gap-2">
              {overflow.map((route) => (
                <button
                  key={route.id}
                  onClick={() => { handleNavigation(route.path); setShowMore(false); }}
                  className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl active:scale-95 transition-all ${
                    activeRoute?.id === route.id ? 'bg-slate-800 text-indigo-400' : 'text-slate-300 hover:bg-slate-800/60'
                  }`}
                >
                  {React.cloneElement(route.icon as React.ReactElement<any>, { size: 22 })}
                  <span className="text-[11px] font-medium tracking-wide text-center leading-tight">{route.desktopLabel}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
